/**
 * Session Manager - Handles token refresh and session validation
 * 
 * This utility provides centralized session management with:
 * - Automatic token refresh before expiry
 * - Invalid refresh token detection and cleanup
 * - Session validation and recovery
 * - CORS and network error handling
 */

import { supabase } from '@/lib/supabase'

interface SessionValidationResult {
  isValid: boolean
  needsRefresh: boolean
  session: any | null
  error?: string
}

/**
 * Clear all auth-related data from localStorage
 */
export const clearAuthStorage = () => {
  if (typeof window === 'undefined') return
  
  console.log('Clearing auth storage...')
  const keysToRemove: string[] = []
  
  // Find all auth-related keys
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i)
    if (key && (
      key.includes('supabase') || 
      key.includes('auth') ||
      key.includes('sb-') // Supabase keys often start with sb-
    )) {
      keysToRemove.push(key)
    }
  }
  
  // Remove all auth keys
  keysToRemove.forEach(key => {
    console.log(`Removing localStorage key: ${key}`)
    window.localStorage.removeItem(key)
  })
  
  // Also clear session storage
  if (window.sessionStorage) {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i)
      if (key && (key.includes('supabase') || key.includes('auth'))) {
        window.sessionStorage.removeItem(key)
      }
    }
  }
}

/**
 * Validate current session and check if it needs refresh
 */
export const validateSession = async (): Promise<SessionValidationResult> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      console.error('Session validation error:', error)
      
      // Check for specific error types
      if (error.status === 400 || error.message?.includes('refresh_token')) {
        clearAuthStorage()
        return {
          isValid: false,
          needsRefresh: false,
          session: null,
          error: 'Invalid session - please sign in again'
        }
      }
      
      return {
        isValid: false,
        needsRefresh: false,
        session: null,
        error: error.message
      }
    }
    
    if (!session) {
      return {
        isValid: false,
        needsRefresh: false,
        session: null,
        error: 'No active session'
      }
    }
    
    // Check if token needs refresh (within 5 minutes of expiry)
    const now = Math.floor(Date.now() / 1000)
    const expiresAt = session.expires_at || 0
    const needsRefresh = (expiresAt - now) < 300 // 5 minutes
    
    return {
      isValid: true,
      needsRefresh,
      session
    }
  } catch (error) {
    console.error('Session validation failed:', error)
    return {
      isValid: false,
      needsRefresh: false,
      session: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Refresh the session token with retry logic
 */
export const refreshSessionToken = async (currentSession?: any): Promise<{ session: any | null; error?: string }> => {
  const maxRetries = 3
  let lastError: any = null
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Attempting token refresh (attempt ${attempt}/${maxRetries})...`)
      
      // Use explicit refresh token if available
      const refreshOptions = currentSession?.refresh_token
        ? { refresh_token: currentSession.refresh_token }
        : undefined
      
      const { data, error } = await supabase.auth.refreshSession(refreshOptions)
      
      if (error) {
        console.error(`Token refresh error (attempt ${attempt}):`, error)
        
        // Handle 400 errors (invalid refresh token)
        if (error.status === 400) {
          console.warn('Invalid refresh token detected')
          clearAuthStorage()
          return {
            session: null,
            error: 'Session expired - please sign in again'
          }
        }
        
        // Handle network errors with retry
        if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
          lastError = error
          if (attempt < maxRetries) {
            console.log(`Network error, retrying in ${attempt * 1000}ms...`)
            await new Promise(resolve => setTimeout(resolve, attempt * 1000))
            continue
          }
        }
        
        // Other errors, don't retry
        return {
          session: null,
          error: error.message
        }
      }
      
      if (data?.session) {
        console.log('Token refreshed successfully')
        return {
          session: data.session
        }
      }
      
      // No session returned
      return {
        session: null,
        error: 'No session returned from refresh'
      }
      
    } catch (error) {
      console.error(`Unexpected error during refresh (attempt ${attempt}):`, error)
      lastError = error
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, attempt * 1000))
        continue
      }
    }
  }
  
  // All retries failed
  return {
    session: null,
    error: lastError?.message || 'Failed to refresh session after multiple attempts'
  }
}

/**
 * Set up automatic session refresh
 */
export const setupAutoRefresh = () => {
  // Check session every minute
  const checkInterval = setInterval(async () => {
    const validation = await validateSession()
    
    if (validation.isValid && validation.needsRefresh) {
      console.log('Session needs refresh, attempting...')
      const result = await refreshSessionToken(validation.session)
      
      if (!result.session) {
        console.error('Auto-refresh failed:', result.error)
        // The auth state listener will handle the logout
      }
    }
  }, 60000) // 1 minute
  
  // Return cleanup function
  return () => clearInterval(checkInterval)
}

/**
 * Handle CORS errors specifically
 */
export const handleCorsError = (error: any): boolean => {
  if (!error) return false
  
  const errorMessage = error.message?.toLowerCase() || ''
  const errorString = error.toString?.().toLowerCase() || ''
  
  return (
    errorMessage.includes('cors') ||
    errorMessage.includes('cross-origin') ||
    errorString.includes('cors') ||
    errorString.includes('cross-origin') ||
    error.status === 0 // Network error often shows as status 0
  )
}

/**
 * Recover from session errors
 */
export const recoverSession = async (): Promise<boolean> => {
  console.log('Attempting session recovery...')
  
  try {
    // First, clear potentially corrupted storage
    clearAuthStorage()
    
    // Try to get a fresh session
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (!error && session) {
      console.log('Session recovered successfully')
      return true
    }
    
    console.log('No recoverable session found')
    return false
  } catch (error) {
    console.error('Session recovery failed:', error)
    return false
  }
}

/**
 * Initialize session management
 */
export const initializeSessionManager = () => {
  // Set up auth state change listener
  const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
    console.log('Auth state changed:', event)
    
    if (event === 'TOKEN_REFRESHED') {
      console.log('Token refreshed via auth state change')
    } else if (event === 'SIGNED_OUT') {
      console.log('User signed out, clearing storage')
      clearAuthStorage()
    } else if (event === 'USER_UPDATED') {
      console.log('User updated')
    }
  })
  
  // Set up auto-refresh
  const cleanupAutoRefresh = setupAutoRefresh()
  
  // Return cleanup function
  return () => {
    authListener?.subscription?.unsubscribe()
    cleanupAutoRefresh()
  }
}

// Export default for convenience
export default {
  clearAuthStorage,
  validateSession,
  refreshSessionToken,
  setupAutoRefresh,
  handleCorsError,
  recoverSession,
  initializeSessionManager
}